/*
 * Minimize imports in this entrypoint to avoid increasing the product bundle size.
 */
import { fg } from '@atlaskit/platform-feature-flags';
import { getMutuallyExclusiveUIExperiment } from '@atlassian/search-experiment';

const JIRA_PRODUCT_NAME = 'jira';

// This is referencing KnowledgeDiscoveryJiraIntentDetectionModel.GNIN_HEURISTIC from @atlassian/search-client package
// Do not import @atlassian/search-client package as it will increase the NIN bundle size. A unit test is associated to this reference.
// Update DEFAULT_MODEL_ID if the default model ID for intent detection changes
const DEFAULT_MODEL_ID = 'GNIN_HEURISTIC';

const navigationCtaVariants = ['control', 'tabs', 'button', 'icons'] as const;
type NavigationCtaVariant = (typeof navigationCtaVariants)[number];
export const navigationCTACohortValidator = (
	cohortValue: unknown,
): cohortValue is NavigationCtaVariant => {
	return Boolean(navigationCtaVariants.find((variant) => cohortValue === variant));
};

export const jiraNINFpsNLQBooleanValidator = (value: unknown): value is boolean => {
	return typeof value === 'boolean';
};

export type ServeJiraNinFpsNavigationResult = {
	cohort: NavigationCtaVariant | undefined;
	hideNavigationCTAOnGNIN?: boolean;
	showQueryInHeader?: boolean;
};

/**
 * A centralised util to check if the Jira NIN FPS navigation experiment is enabled
 */
export const serveJiraNinFpsNavigation = ({
	primaryProduct,
	fireLayerExposure = true,
}: {
	fireLayerExposure?: boolean;
	primaryProduct?: string | null;
} = {}): ServeJiraNinFpsNavigationResult | undefined => {
	if (primaryProduct !== JIRA_PRODUCT_NAME) {
		return undefined;
	}

	// cohortForIntentDetectionExperiment AKA Experiment 2
	const intentDetectionExperiment = serveJiraNINFpsNLQNavigation({
		primaryProduct,
		fireLayerExposure: false, // Exposure should only be fired when intent detection has occured on Quick Find
	});
	const cohortForIntentDetectionExperiment = intentDetectionExperiment?.uiFeatures.navigationCTA;

	// Intent detection experiment takes priority on what CTA is used.
	// For example: If the Jira NIN FPS navigation experiment is decided and we start experiment 2, we still want to be able to set
	// different navigation CTAs in case we want to test different CTA variants in experiment 2.
	if (cohortForIntentDetectionExperiment) {
		return {
			cohort: cohortForIntentDetectionExperiment,
			hideNavigationCTAOnGNIN: intentDetectionExperiment?.uiFeatures?.hideNavigationCTAOnGNIN,
			showQueryInHeader: intentDetectionExperiment?.uiFeatures?.showQueryInHeader,
		};
	}

	// This gate is used to ensure that certain audiences (i.e. CentralAI) are included in the Jira UI experiment.
	// Without it, some users might be excluded due to layer allocation opting out potential candidates.
	// The gate guarantees participation of the targeted audience during the initial experimentation phase.
	if (!fg('rovo_jira_ui_experience_audience_gate')) {
		return undefined;
	}

	const layer = getMutuallyExclusiveUIExperiment(
		'rovo_jira_nin_fps_navigation_exp',
		'rovo_jira_ui_experience_layer',
		{ fireLayerExposure },
	);

	// cohortForNinFpsExperiment AKA Experiment 1
	const cohortForNinFpsExperiment = layer?.get('cohort', undefined, navigationCTACohortValidator);
	return {
		cohort: cohortForNinFpsExperiment,
		hideNavigationCTAOnGNIN: undefined,
		showQueryInHeader: undefined,
	};
};

type ServeJiraNINFpsNLQNavigationProps = {
	fireLayerExposure?: boolean;
	primaryProduct?: string | null;
};

export const DEFAULT_NLQ_NAVIGATION_UI_FEATURES = {
	navigationCTA: 'button' satisfies NavigationCtaVariant,
	hideFilterSuggestions: true,
	hideNavigationCTAOnGNIN: false,
	showMatchingWorkItem: true,
	showQueryInHeader: true,
	maxRecentQueryCountWhenNoSuggestions: 1,
} as const;

type NLQNavigationUIFeatures = typeof DEFAULT_NLQ_NAVIGATION_UI_FEATURES & {
	enableControlIntentDetectionAnalytics?: boolean;
};

const extractNLQNavigationLayerParams = (
	layer: ReturnType<typeof getMutuallyExclusiveUIExperiment>,
) => {
	const variants = ['control', 'test'] as const;
	const cohort = layer?.get(
		'cohort',
		undefined,
		(cohortValue: unknown): cohortValue is (typeof variants)[number] => {
			return Boolean(variants.find((variant) => cohortValue === variant));
		},
	);

	const uiFeaturesFromStatsig =
		layer?.get(
			'uiFeatures',
			{},
			(
				uiFeaturesValue: unknown,
			): uiFeaturesValue is Pick<
				NLQNavigationUIFeatures,
				'enableControlIntentDetectionAnalytics'
			> => {
				if (!uiFeaturesValue || typeof uiFeaturesValue !== 'object') {
					return false;
				}
				const isEnableControlIntentDetectionAnalyticsValid =
					'enableControlIntentDetectionAnalytics' in uiFeaturesValue
						? jiraNINFpsNLQBooleanValidator(uiFeaturesValue.enableControlIntentDetectionAnalytics)
						: true; // Undefined values are valid

				return isEnableControlIntentDetectionAnalyticsValid;
			},
		) ?? {};

	const uiFeaturesWithDefaults = {
		...uiFeaturesFromStatsig,
		...DEFAULT_NLQ_NAVIGATION_UI_FEATURES,
	} satisfies NLQNavigationUIFeatures;

	const modelId =
		layer?.get(
			'modelId',
			DEFAULT_MODEL_ID,
			(modelIdValue: unknown): modelIdValue is string =>
				typeof modelIdValue === 'string' && modelIdValue.length > 0,
		) ?? DEFAULT_MODEL_ID;

	return {
		cohort,
		uiFeatures: uiFeaturesWithDefaults,
		modelId,
	};
};

const serveJiraNINFpsNLQNavigationUserBased = ({
	primaryProduct,
	fireLayerExposure = true,
}: ServeJiraNINFpsNLQNavigationProps = {}) => {
	if (primaryProduct !== JIRA_PRODUCT_NAME) {
		return undefined;
	}

	// This gate is used to ensure that certain audiences (i.e. CentralAI) are included in the Jira UI experiment.
	// Without it, some users might be excluded due to layer allocation opting out potential candidates.
	// The gate guarantees participation of the targeted audience during the initial experimentation phase.
	if (!fg('rovo_jira_intent_detection_audience_gate')) {
		return undefined;
	}

	const layer = getMutuallyExclusiveUIExperiment(
		'rovo_jira_nin_fps_nlq_navigation_exp',
		'rovo_jira_ui_experience_layer',
		{ fireLayerExposure },
	);
	return extractNLQNavigationLayerParams(layer);
};

/**
 * A centralised util to check if the Jira NIN FPS Intent detection experiment is enabled
 */
export const serveJiraNINFpsNLQNavigation = (
	props: ServeJiraNINFpsNLQNavigationProps = {},
):
	| {
			cohort: 'test' | 'control' | undefined;
			uiFeatures: NLQNavigationUIFeatures;
			modelId: string;
	  }
	| undefined => serveJiraNINFpsNLQNavigationUserBased(props);

/**
 * Returns whether intent detection analytics should be tracked for the current user.
 * True when the user is in the 'test' cohort of the intent detection experiment,
 * or in the 'control' cohort with the enableControlIntentDetectionAnalytics flag enabled.
 *
 * Used by FPS and NIN surfaces.
 */
export const isIntentDetectionTriggeredForExperiment = (
	primaryProduct?: string | null,
): boolean => {
	const experiment = serveJiraNINFpsNLQNavigation({
		primaryProduct,
		fireLayerExposure: false,
	});
	return (
		experiment?.cohort === 'test' ||
		(experiment?.cohort === 'control' &&
			(experiment?.uiFeatures.enableControlIntentDetectionAnalytics ?? false))
	);
};
